/**
 * Mock attestation generator — fabricates a plausible stream of
 * attestations per GI so the viewer has rich data to render.
 * Deterministically seeded by the GI's root_id so reloads are stable.
 */

import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

import type {
  AttestationRecordFull,
  EntityId,
  Hash256,
  WitnessStatus,
} from '../types';

import { getRecord, nowTimestamp } from './store';

const MOCK_LATENCY_MS = 160;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface AttestationFilters {
  from_iso?: string;
  to_iso?: string;
  witness_status?: WitnessStatus;
}

export async function listAttestations(
  root_id: EntityId,
  filters: AttestationFilters = {},
): Promise<AttestationRecordFull[]> {
  await sleep(MOCK_LATENCY_MS);
  const rec = getRecord(root_id);
  if (!rec) return [];
  const all = synthesize(root_id);
  return all.filter((a) => {
    if (filters.witness_status && a.witness_status !== filters.witness_status) return false;
    if (filters.from_iso && a.signed_at.iso < filters.from_iso) return false;
    if (filters.to_iso && a.signed_at.iso > filters.to_iso) return false;
    return true;
  });
}

/** Fabricate attestations deterministically from `root_id`. */
function synthesize(root_id: EntityId): AttestationRecordFull[] {
  const now = Date.now();
  const count = 30 + (sum_chars(root_id) % 30);
  const records: AttestationRecordFull[] = [];
  for (let i = 0; i < count; i++) {
    const minutes_ago = i * 47 + (sum_chars(root_id + String(i)) % 23);
    const iso = new Date(now - minutes_ago * 60_000).toISOString();
    const seed = `${root_id}#${i}`;
    const input_hash = hashOf(`${seed}/in`);
    const output_hash = hashOf(`${seed}/out`);
    const wasm_hash = hashOf(`${root_id}/wasm`);
    const fuel_consumed = 12_000 + ((sum_chars(seed)) % 180_000);
    const bytes_in = 512 + ((sum_chars(`${seed}/bi`)) % 4096);
    const bytes_out = 256 + ((sum_chars(`${seed}/bo`)) % 2048);
    const ws: WitnessStatus =
      i % 17 === 3 ? 'down' : i % 11 === 5 ? 'pending' : 'ok';
    records.push({
      id: `att-${root_id.slice(0, 8)}-${count - i}`,
      root_id,
      version: '1.0.0',
      wasm_hash,
      input_hash,
      output_hash,
      fuel_consumed,
      wasmtime_version: 'wasmtime-35',
      signed_at: { iso, source: 'RFC3161' },
      engine_signature: `sig:${bytesToHex(sha256(new TextEncoder().encode(seed))).slice(0, 128)}` as AttestationRecordFull['engine_signature'],
      witness_status: ws,
      execution_label: `execution_${count - i}`,
      bytes_in,
      bytes_out,
    });
  }
  // newest first
  return records.sort((a, b) => b.signed_at.iso.localeCompare(a.signed_at.iso));
}

function hashOf(input: string): Hash256 {
  return `sha256:${bytesToHex(sha256(new TextEncoder().encode(input)))}`;
}
function sum_chars(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) n = (n + s.charCodeAt(i)) | 0;
  return Math.abs(n);
}

// Keep `nowTimestamp` imported — used in a future "add attestation"
// flow. Silent reference.
void nowTimestamp;
